/**
 * Space Yield Report — Pure computation functions
 *
 * All functions are pure (no side effects, no Supabase calls).
 * Input: raw query results + period. Output: ProcessedUnit[], KpiData, UnitGroup[].
 */

import {
  startOfMonth,
  endOfMonth,
  addDays,
  differenceInDays,
  differenceInCalendarDays,
  max as dateMax,
  min as dateMin,
  isSameMonth,
  parseISO,
  getDaysInMonth,
} from 'date-fns'
import type {
  RawUnit,
  RawLease,
  RawTransientStay,
  RawFuelTransaction,
  RawAllocationWithFootprint,
  ProcessedUnit,
  UnitGroup,
  KpiData,
  UnitStatus,
  RevenueSegment,
  GroupByOption,
  SpaceYieldReport,
  DbUnitType,
  RenewalStatus,
  DisplaySubRow,
  LeaseDisplayRow,
  TransientAggRow,
  YieldComparison,
} from './types'

// ─── Helpers ─────────────────────────────────────────────────────────

/** Compute effective sqft: usable_sqft || door_width * depth fallback */
export function computeUnitSqft(unit: RawUnit): number {
  if (unit.usable_sqft && unit.usable_sqft > 0) return unit.usable_sqft
  if (unit.door_width && unit.depth) return unit.door_width * unit.depth
  return 0
}

/** Count units per DB unit_type (before any filtering) */
export function countUnitTypes(rawUnits: RawUnit[]): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const u of rawUnits) {
    if (u.type) {
      counts[u.type] = (counts[u.type] || 0) + 1
    }
  }
  return counts
}

/** Get display name for a client from raw join */
function getClientName(client: { company_name: string | null; first_name: string | null; last_name: string | null } | null): string | undefined {
  if (!client) return undefined
  if (client.company_name) return client.company_name
  const parts = [client.first_name, client.last_name].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : undefined
}

// ─── Transient Revenue Proration ─────────────────────────────────────

/**
 * Prorate a transient stay's subtotal to the selected month.
 * nights_in_month / total_nights * subtotal
 */
export function prorateTranRevenue(
  stay: RawTransientStay,
  monthStart: Date,
  monthEnd: Date,
): number {
  const subtotal = stay.subtotal
  if (!subtotal || subtotal <= 0) return 0

  const nightsCount = stay.nights_count
  if (!nightsCount || nightsCount <= 0) return 0

  const stayStart = parseISO(stay.check_in)
  const stayEnd = stay.actual_check_out
    ? parseISO(stay.actual_check_out)
    : parseISO(stay.expected_check_out)

  // Overlap window: clamp stay to month boundaries
  const overlapStart = dateMax([stayStart, monthStart])
  const overlapEnd = dateMin([stayEnd, addDays(monthEnd, 1)])
  const nightsInMonth = Math.max(0, differenceInDays(overlapEnd, overlapStart))

  return nightsCount > 0 ? subtotal * (nightsInMonth / nightsCount) : 0
}

/**
 * Compute number of overlap nights for a transient stay in the selected month.
 */
function computeOverlapNights(
  stay: RawTransientStay,
  monthStart: Date,
  monthEnd: Date,
): number {
  const stayStart = parseISO(stay.check_in)
  const stayEnd = stay.actual_check_out
    ? parseISO(stay.actual_check_out)
    : parseISO(stay.expected_check_out)
  const overlapStart = dateMax([stayStart, monthStart])
  const overlapEnd = dateMin([stayEnd, addDays(monthEnd, 1)])
  return Math.max(0, differenceInDays(overlapEnd, overlapStart))
}

/**
 * Compute unique occupied nights by merging overlapping stay intervals.
 * Clamps each stay to [monthStart, monthEnd+1day), sorts, merges overlaps,
 * then sums unique calendar nights.
 */
export function computeUniqueOccupiedNights(
  stays: RawTransientStay[],
  monthStart: Date,
  monthEnd: Date,
): number {
  const intervals: [number, number][] = []
  const clampEnd = addDays(monthEnd, 1)

  for (const stay of stays) {
    const stayStart = parseISO(stay.check_in)
    const stayEnd = stay.actual_check_out
      ? parseISO(stay.actual_check_out)
      : parseISO(stay.expected_check_out)

    const overlapStart = dateMax([stayStart, monthStart])
    const overlapEnd = dateMin([stayEnd, clampEnd])

    if (overlapEnd > overlapStart) {
      intervals.push([overlapStart.getTime(), overlapEnd.getTime()])
    }
  }

  if (intervals.length === 0) return 0

  intervals.sort((a, b) => a[0] - b[0])

  const merged: [number, number][] = [intervals[0]]
  for (let i = 1; i < intervals.length; i++) {
    const last = merged[merged.length - 1]
    if (intervals[i][0] <= last[1]) {
      last[1] = Math.max(last[1], intervals[i][1])
    } else {
      merged.push(intervals[i])
    }
  }

  let total = 0
  for (const [start, end] of merged) {
    total += differenceInDays(new Date(end), new Date(start))
  }

  return total
}

// ─── Status Classification ───────────────────────────────────────────

/**
 * Classify a unit's status based on leases, transient revenue, and allocations.
 * Accepts an array of leases (a unit can have multiple active leases).
 * Priority order from spec (lower = higher priority):
 *   0: maintenance, 1: occupied_no_contract, 2: expiring, 3: underpriced,
 *   4: leased, 5: month_to_month, 6: transient, 7: idle
 *
 * When multiple leases exist, the highest-priority (worst) status wins.
 */
export function classifyStatus(
  unitStatus: string | null,
  leases: RawLease[],
  _transientRevenue: number,
  hasTransientStay: boolean,
  monthEnd: Date,
): UnitStatus {
  // P0: Maintenance
  if (unitStatus === 'maintenance') return 'maintenance'

  if (leases.length > 0) {
    // Check if ANY lease is expiring (P2 — highest lease priority)
    const hasExpiring = leases.some((l) => {
      if (l.type === 'month_to_month' || !l.end_date) return false
      const d = differenceInCalendarDays(parseISO(l.end_date), monthEnd)
      return d >= 0 && d <= 90
    })
    if (hasExpiring) return 'expiring'

    // If all leases are M2M → month_to_month; otherwise → leased
    const allM2M = leases.every((l) => l.type === 'month_to_month')
    if (allM2M) return 'month_to_month'
    return 'leased'
  }

  // P6: Transient (no lease, has active transient stay)
  if (hasTransientStay) return 'transient'

  // P7: Idle
  return 'idle'
}

// ─── Renewal Status Derivation ──────────────────────────────────────

/**
 * Derive renewal status from an array of leases.
 * Worst-case wins: Not Started > Contacted > Auto-Renew > —
 */
function deriveRenewalStatus(leases: RawLease[]): RenewalStatus {
  if (leases.length === 0) return '—'
  let hasNotStarted = false
  let hasContacted = false
  let hasAutoRenew = false
  for (const lease of leases) {
    if (lease.auto_renew) { hasAutoRenew = true; continue }
    if (lease.renewal_contacted_date) { hasContacted = true; continue }
    if (lease.status === 'expiring') { hasNotStarted = true }
  }
  if (hasNotStarted) return 'Not Started'
  if (hasContacted) return 'Contacted'
  if (hasAutoRenew) return 'Auto-Renew'
  return '—'
}

// ─── Underpriced Detection ───────────────────────────────────────────

/**
 * Detect underpriced units within the same building and type.
 * Threshold: yield/sqft < 80% of same-type, same-building average.
 * Only lease units can be flagged; transient-only units are excluded.
 * Also computes rate_gap_pct for every lease unit that has peers.
 */
export function detectUnderpriced(units: ProcessedUnit[]): ProcessedUnit[] {
  // Group by area_id + unit_type
  const groups = new Map<string, ProcessedUnit[]>()
  for (const u of units) {
    const key = `${u.area_id}::${u.unit_type}`
    const arr = groups.get(key) || []
    arr.push(u)
    groups.set(key, arr)
  }

  return units.map((u) => {
    // Only lease units can be underpriced / get rate_gap
    if (u.revenue_segment !== 'lease') return u
    if (u.yield_per_sqft <= 0) return u

    const key = `${u.area_id}::${u.unit_type}`
    const peers = groups.get(key) || []
    // Producing peers only (lease + transient, not idle/maintenance)
    const producing = peers.filter(
      (p) => p.revenue_segment !== 'idle' && p.yield_per_sqft > 0
    )
    if (producing.length < 2) return u // Need at least 2 to compare

    const avgYield = producing.reduce((s, p) => s + p.yield_per_sqft, 0) / producing.length
    const threshold = avgYield * 0.8
    const rateGapPct = avgYield > 0 ? (u.yield_per_sqft / avgYield) * 100 : undefined

    if (u.yield_per_sqft < threshold) {
      return { ...u, is_underpriced: true, status: 'underpriced' as UnitStatus, rate_gap_pct: rateGapPct }
    }
    return { ...u, rate_gap_pct: rateGapPct }
  })
}

// ─── Vacancy Cost Computation ───────────────────────────────────────

/**
 * Compute vacancy_cost for idle units based on avg yield of producing peers of the same type.
 */
function computeVacancyCost(units: ProcessedUnit[]): ProcessedUnit[] {
  // Group producing peers by unit_type to get avg yield
  const yieldByType = new Map<string, { totalYield: number; totalSqft: number }>()
  for (const u of units) {
    if (u.revenue_segment === 'idle' || u.yield_per_sqft <= 0) continue
    const typeKey = u.unit_type || 'unknown'
    const acc = yieldByType.get(typeKey) || { totalYield: 0, totalSqft: 0 }
    acc.totalYield += (u.lease_rate || 0) + (u.transient_revenue || 0)
    acc.totalSqft += u.sqft
    yieldByType.set(typeKey, acc)
  }

  return units.map((u) => {
    if (u.status !== 'idle' || u.sqft <= 0) return u
    const typeKey = u.unit_type || 'unknown'
    const acc = yieldByType.get(typeKey)
    if (!acc || acc.totalSqft <= 0) return u
    const avgMarketYield = acc.totalYield / acc.totalSqft
    return { ...u, vacancy_cost: u.sqft * avgMarketYield }
  })
}

// ─── Main Pipeline ───────────────────────────────────────────────────

export function computeReport(
  rawUnits: RawUnit[],
  rawLeases: RawLease[],
  rawTransients: RawTransientStay[],
  rawFuelTransactions: RawFuelTransaction[],
  rawAllocations: RawAllocationWithFootprint[],
  period: string,           // YYYY-MM
  typesFilter: DbUnitType[],
  segmentFilter: 'all' | RevenueSegment,
  groupBy: GroupByOption,
  statusFilter?: UnitStatus,
): SpaceYieldReport {
  const monthDate = parseISO(`${period}-01`)
  const monthStart = startOfMonth(monthDate)
  const monthEnd = endOfMonth(monthDate)
  const now = new Date()
  const isMtd = isSameMonth(monthDate, now) && now < monthEnd
  const daysInMonth = getDaysInMonth(monthDate)

  // Count unit types before filtering
  const unitTypeCounts = countUnitTypes(rawUnits)

  // Build type filter set
  const typesSet = new Set(typesFilter)

  // Index leases by unit_id (multiple leases per unit)
  const leasesByUnit = new Map<string, RawLease[]>()
  for (const l of rawLeases) {
    if (l.unit_id) {
      const arr = leasesByUnit.get(l.unit_id) || []
      arr.push(l)
      leasesByUnit.set(l.unit_id, arr)
    }
  }

  // Aggregate transient revenue + stays per unit
  const transientRevByUnit = new Map<string, number>()
  const transientStayByUnit = new Map<string, RawTransientStay[]>()
  for (const ts of rawTransients) {
    if (!ts.unit_id) continue
    const rev = prorateTranRevenue(ts, monthStart, monthEnd)
    transientRevByUnit.set(ts.unit_id, (transientRevByUnit.get(ts.unit_id) || 0) + rev)
    const stays = transientStayByUnit.get(ts.unit_id) || []
    stays.push(ts)
    transientStayByUnit.set(ts.unit_id, stays)
  }

  // ── Fuel indexing ──────────────────────────────────────────────────
  // Build reverse maps: lease_id → unit_id, transient_stay_id → unit_id
  const leaseIdToUnitId = new Map<string, string>()
  for (const l of rawLeases) {
    if (l.unit_id) leaseIdToUnitId.set(l.id, l.unit_id)
  }
  const transientStayIdToUnitId = new Map<string, string>()
  for (const ts of rawTransients) {
    if (ts.unit_id) transientStayIdToUnitId.set(ts.id, ts.unit_id)
  }

  // Aggregate fuel by unit, by lease, by transient stay
  const fuelRevByUnit = new Map<string, number>()
  const fuelGalByUnit = new Map<string, number>()
  const fuelRevByLease = new Map<string, number>()
  const fuelGalByLease = new Map<string, number>()
  const fuelRevByTransientStay = new Map<string, number>()
  const fuelGalByTransientStay = new Map<string, number>()
  let unattributedFuelRev = 0

  for (const ft of rawFuelTransactions) {
    const amount = Number(ft.total_amount) || 0
    const gallons = Number(ft.quantity_gallons) || 0
    let unitId: string | undefined

    if (ft.lease_contract_id) {
      unitId = leaseIdToUnitId.get(ft.lease_contract_id)
      fuelRevByLease.set(ft.lease_contract_id, (fuelRevByLease.get(ft.lease_contract_id) || 0) + amount)
      fuelGalByLease.set(ft.lease_contract_id, (fuelGalByLease.get(ft.lease_contract_id) || 0) + gallons)
    } else if (ft.transient_stay_id) {
      unitId = transientStayIdToUnitId.get(ft.transient_stay_id)
      fuelRevByTransientStay.set(ft.transient_stay_id, (fuelRevByTransientStay.get(ft.transient_stay_id) || 0) + amount)
      fuelGalByTransientStay.set(ft.transient_stay_id, (fuelGalByTransientStay.get(ft.transient_stay_id) || 0) + gallons)
    }

    if (unitId) {
      fuelRevByUnit.set(unitId, (fuelRevByUnit.get(unitId) || 0) + amount)
      fuelGalByUnit.set(unitId, (fuelGalByUnit.get(unitId) || 0) + gallons)
    } else {
      unattributedFuelRev += amount
    }
  }

  // Fuel commitment by unit (from lease fuel_commitment_gallons_monthly)
  const fuelCommitmentByUnit = new Map<string, number>()
  for (const l of rawLeases) {
    if (l.unit_id && l.fuel_commitment_gallons_monthly && l.fuel_commitment_gallons_monthly > 0) {
      fuelCommitmentByUnit.set(
        l.unit_id,
        (fuelCommitmentByUnit.get(l.unit_id) || 0) + l.fuel_commitment_gallons_monthly,
      )
    }
  }

  // ── Footprint indexing (for occupancy) ─────────────────────────────
  // Build footprintsByUnit: unit_id → sum of aircraft footprint_sqft
  const footprintsByUnit = new Map<string, number>()
  for (const alloc of rawAllocations) {
    if (!alloc.unit_id) continue
    let footprint = 0
    const acType = alloc.aircraft?.aircraft_types
    if (acType) {
      if (acType.footprint_sqft && acType.footprint_sqft > 0) {
        footprint = acType.footprint_sqft
      } else if (acType.wingspan > 0 && acType.length > 0) {
        // Rough rectangular footprint estimate
        footprint = acType.wingspan * acType.length * 0.7
      }
    }
    if (footprint > 0) {
      footprintsByUnit.set(alloc.unit_id, (footprintsByUnit.get(alloc.unit_id) || 0) + footprint)
    }
  }

  // Process each unit
  let processed: ProcessedUnit[] = rawUnits
    .filter((u) => {
      // Filter by granular unit types
      return u.type !== null && typesSet.has(u.type)
    })
    .map((u) => {
      const sqft = computeUnitSqft(u)
      const leases = leasesByUnit.get(u.id) || []
      const transientRev = transientRevByUnit.get(u.id) || 0
      const transientStays = transientStayByUnit.get(u.id) || []
      const hasTransientStay = transientStays.length > 0

      const status = classifyStatus(u.status, leases, transientRev, hasTransientStay, monthEnd)

      // Revenue segment
      let revenue_segment: RevenueSegment = 'idle'
      if (leases.length > 0) revenue_segment = 'lease'
      else if (hasTransientStay) revenue_segment = 'transient'

      // Tenant name — leases take priority
      let tenant_name: string | undefined
      if (leases.length === 1) {
        tenant_name = getClientName(leases[0].clients)
      } else if (leases.length > 1) {
        // Collect unique tenant names across leases
        const names = [...new Set(leases.map((l) => getClientName(l.clients)).filter(Boolean))] as string[]
        if (names.length === 1) {
          tenant_name = names[0]
        } else if (names.length === 2) {
          tenant_name = `${names[0]} + 1 more`
        } else if (names.length > 2) {
          tenant_name = `${names.length} clients`
        }
      } else if (transientStays.length === 1) {
        tenant_name = getClientName(transientStays[0].clients)
      } else if (transientStays.length > 1) {
        const firstName = getClientName(transientStays[0].clients)
        tenant_name = firstName ? `${firstName} (+${transientStays.length - 1})` : `(${transientStays.length} stays)`
      }

      // Aggregate lease revenue from ALL leases
      const leaseRate = leases.reduce((sum, l) => sum + (l.monthly_rate || 0), 0)
      const totalRev = leaseRate + transientRev
      const yield_per_sqft = sqft > 0 ? totalRev / sqft : 0

      // Days to expiry — earliest expiring lease wins
      let days_to_expiry: number | undefined
      for (const l of leases) {
        if (l.type !== 'month_to_month' && l.end_date) {
          const d = differenceInCalendarDays(parseISO(l.end_date), monthEnd)
          if (d >= 0 && (days_to_expiry === undefined || d < days_to_expiry)) {
            days_to_expiry = d
          }
        }
      }

      // Lease end — earliest end date
      let lease_end: string | undefined
      for (const l of leases) {
        if (l.end_date && (!lease_end || l.end_date < lease_end)) {
          lease_end = l.end_date
        }
      }

      // Lease type — if mixed, pick the primary (first non-M2M, or first)
      const primaryLease = leases.find((l) => l.type !== 'month_to_month') || leases[0]

      // Extended fields
      const annual_value = (leaseRate + transientRev) * 12

      // Transient avg rate per night — aggregate subtotal / nights_count across all stays for this unit
      let trans_rate_per_night: number | undefined
      if (transientStays.length > 0) {
        let totalSubtotal = 0
        let totalNights = 0
        for (const ts of transientStays) {
          if (ts.subtotal && ts.nights_count && ts.nights_count > 0) {
            totalSubtotal += ts.subtotal
            totalNights += ts.nights_count
          }
        }
        if (totalNights > 0) {
          trans_rate_per_night = totalSubtotal / totalNights
        }
      }

      // Utilization: unique occupied nights (deduplicated overlaps) / days_in_month
      let utilization_pct: number | undefined
      let transient_overlap_nights: number | undefined
      if (transientStays.length > 0) {
        const uniqueNights = computeUniqueOccupiedNights(transientStays, monthStart, monthEnd)
        transient_overlap_nights = uniqueNights
        utilization_pct = Math.min(100, Math.round((uniqueNights / daysInMonth) * 100))
      }

      // Renewal status (worst-case across all leases)
      const renewal_status = deriveRenewalStatus(leases)

      // Escalation — from primary lease
      const escalation_pct = primaryLease?.escalation_pct ?? undefined
      const escalation_type = primaryLease?.escalation_type ?? undefined

      // Fuel overlay fields
      const unitFuelRev = fuelRevByUnit.get(u.id) || 0
      const unitFuelGal = fuelGalByUnit.get(u.id) || 0
      const unitFuelCommitment = fuelCommitmentByUnit.get(u.id) || 0
      const totalTenantValue = leaseRate + transientRev + unitFuelRev
      const totalValuePerSqft = sqft > 0 ? totalTenantValue / sqft : 0

      // Footprint-based occupancy
      const unitFootprint = footprintsByUnit.get(u.id) || 0
      const safetyFootprint = unitFootprint * 1.05
      let occupancy_pct: number
      let occupied_sqft: number

      if (u.capacity_strategy === 'single') {
        // Single: 100% if has any contract, 0% otherwise
        const hasContract = leases.length > 0 || hasTransientStay
        occupancy_pct = hasContract ? 100 : 0
        occupied_sqft = hasContract ? sqft : 0
      } else {
        // Stackable / area_based: footprint-based
        occupancy_pct = sqft > 0 ? Math.min(100, Math.round((safetyFootprint / sqft) * 100)) : 0
        occupied_sqft = Math.min(sqft, safetyFootprint)
      }

      return {
        unit_id: u.id,
        unit_code: u.unit_code,
        unit_type: u.type,
        area_id: u.area_id,
        area_name: u.areas?.name || 'Unknown',
        sqft,
        capacity_strategy: u.capacity_strategy,
        status,
        revenue_segment,
        tenant_name,
        lease_rate: leaseRate > 0 ? leaseRate : undefined,
        transient_revenue: transientRev > 0 ? transientRev : undefined,
        yield_per_sqft,
        lease_end,
        days_to_expiry,
        lease_type: primaryLease?.type,
        contract_number: primaryLease?.contract_number,
        is_underpriced: false,
        is_mtd: isMtd,
        // Fuel overlay
        fuel_revenue: unitFuelRev > 0 ? unitFuelRev : undefined,
        fuel_gallons: unitFuelGal > 0 ? unitFuelGal : undefined,
        fuel_commitment_gallons: unitFuelCommitment > 0 ? unitFuelCommitment : undefined,
        fuel_delivered_gallons: unitFuelGal > 0 ? unitFuelGal : undefined,
        fuel_commitment_pct: unitFuelCommitment > 0 ? Math.round((unitFuelGal / unitFuelCommitment) * 100) : undefined,
        total_tenant_value: totalTenantValue > 0 ? totalTenantValue : undefined,
        total_value_per_sqft: totalTenantValue > 0 ? totalValuePerSqft : undefined,
        // Extended yield fields
        annual_value: annual_value > 0 ? annual_value : undefined,
        trans_rate_per_night,
        utilization_pct,
        transient_overlap_nights,
        renewal_status,
        escalation_pct: escalation_pct != null ? escalation_pct : undefined,
        escalation_type,
        // Footprint-based occupancy
        occupied_sqft,
        occupancy_pct,
      } satisfies ProcessedUnit
    })

  // Detect underpriced (also sets rate_gap_pct)
  processed = detectUnderpriced(processed)

  // Compute vacancy cost for idle units
  processed = computeVacancyCost(processed)

  // Apply segment filter
  if (segmentFilter !== 'all') {
    processed = processed.filter((u) => u.revenue_segment === segmentFilter)
  }

  // Apply status quick filter
  if (statusFilter) {
    processed = processed.filter((u) => u.status === statusFilter)
  }

  // Has any transient data?
  const has_transient_data = processed.some((u) => u.transient_revenue && u.transient_revenue > 0)
  const has_fuel_data = rawFuelTransactions.length > 0

  // Compute groups
  const groups = groupUnits(processed, groupBy, daysInMonth)

  // MVP: building mode uses flat unit rows (no sub-row enrichment)
  // enrichBuildingGroups kept for future use but not called

  // Compute KPIs (before segment/status filters — use full processed set)
  // Re-process from raw for anchored KPIs
  const kpiUnits = rawUnits
    .filter((u) => u.type !== null && typesSet.has(u.type!))
  const allFiltered = kpiUnits
    .map((u) => {
      const sqft = computeUnitSqft(u)
      const leases = leasesByUnit.get(u.id) || []
      const transientRev = transientRevByUnit.get(u.id) || 0
      const transientStays = transientStayByUnit.get(u.id) || []
      const hasTransientStay = transientStays.length > 0
      const status = classifyStatus(u.status, leases, transientRev, hasTransientStay, monthEnd)
      let revenue_segment: RevenueSegment = 'idle'
      if (leases.length > 0) revenue_segment = 'lease'
      else if (hasTransientStay) revenue_segment = 'transient'
      const leaseRate = leases.reduce((sum, l) => sum + (l.monthly_rate || 0), 0)
      const totalRev = leaseRate + transientRev
      const yield_per_sqft = sqft > 0 ? totalRev / sqft : 0
      let days_to_expiry: number | undefined
      for (const l of leases) {
        if (l.type !== 'month_to_month' && l.end_date) {
          const d = differenceInCalendarDays(parseISO(l.end_date), monthEnd)
          if (d >= 0 && (days_to_expiry === undefined || d < days_to_expiry)) {
            days_to_expiry = d
          }
        }
      }
      const primaryLease = leases.find((l) => l.type !== 'month_to_month') || leases[0]
      // Footprint-based occupancy for KPI units
      const kpiFootprint = footprintsByUnit.get(u.id) || 0
      const kpiSafetyFp = kpiFootprint * 1.05
      const kpiCapStrategy = u.capacity_strategy
      let kpiOccupiedSqft: number
      let kpiOccupancyPct: number
      if (kpiCapStrategy === 'single') {
        const hasContract = leases.length > 0 || hasTransientStay
        kpiOccupiedSqft = hasContract ? sqft : 0
        kpiOccupancyPct = hasContract ? 100 : 0
      } else {
        kpiOccupancyPct = sqft > 0 ? Math.min(100, Math.round((kpiSafetyFp / sqft) * 100)) : 0
        kpiOccupiedSqft = Math.min(sqft, kpiSafetyFp)
      }
      // Transient fields for yield comparison
      const transientOverlapNights = transientStays.length > 0
        ? computeUniqueOccupiedNights(transientStays, monthStart, monthEnd) : undefined
      let transRatePerNight: number | undefined
      if (transientStays.length > 0) {
        let tSub = 0, tNts = 0
        for (const ts of transientStays) {
          if (ts.subtotal && ts.nights_count && ts.nights_count > 0) { tSub += ts.subtotal; tNts += ts.nights_count }
        }
        if (tNts > 0) transRatePerNight = tSub / tNts
      }
      return { sqft, leaseRate, transientRev, status, revenue_segment, yield_per_sqft, days_to_expiry, primaryLease, kpiOccupiedSqft, kpiOccupancyPct, transientOverlapNights, transRatePerNight }
    })

  const kpi = computeKpis(detectUnderpriced(
    allFiltered.map((u, i) => ({
      unit_id: kpiUnits[i]?.id ?? '',
      unit_code: '',
      unit_type: kpiUnits[i]?.type ?? null,
      area_id: kpiUnits[i]?.area_id ?? '',
      area_name: '',
      sqft: u.sqft,
      capacity_strategy: null,
      status: u.status as UnitStatus,
      revenue_segment: u.revenue_segment,
      lease_rate: u.leaseRate > 0 ? u.leaseRate : undefined,
      transient_revenue: u.transientRev > 0 ? u.transientRev : undefined,
      yield_per_sqft: u.yield_per_sqft,
      lease_end: u.primaryLease?.end_date ?? undefined,
      days_to_expiry: u.days_to_expiry,
      lease_type: u.primaryLease?.type,
      contract_number: u.primaryLease?.contract_number,
      is_underpriced: false,
      is_mtd: isMtd,
      occupied_sqft: u.kpiOccupiedSqft,
      occupancy_pct: u.kpiOccupancyPct,
    }))
  ))

  // Compute yield comparison from KPI-anchored units (full set, before segment/status filters)
  const yield_comparison = computeYieldComparison(detectUnderpriced(
    allFiltered.map((u, i) => ({
      unit_id: kpiUnits[i]?.id ?? '',
      unit_code: '',
      unit_type: kpiUnits[i]?.type ?? null,
      area_id: kpiUnits[i]?.area_id ?? '',
      area_name: '',
      sqft: u.sqft,
      capacity_strategy: null,
      status: u.status as UnitStatus,
      revenue_segment: u.revenue_segment,
      lease_rate: u.leaseRate > 0 ? u.leaseRate : undefined,
      transient_revenue: u.transientRev > 0 ? u.transientRev : undefined,
      yield_per_sqft: u.yield_per_sqft,
      lease_end: u.primaryLease?.end_date ?? undefined,
      days_to_expiry: u.days_to_expiry,
      lease_type: u.primaryLease?.type,
      contract_number: u.primaryLease?.contract_number,
      is_underpriced: false,
      is_mtd: isMtd,
      fuel_revenue: fuelRevByUnit.get(kpiUnits[i]?.id ?? '') || undefined,
      transient_overlap_nights: u.transientOverlapNights,
      trans_rate_per_night: u.transRatePerNight,
      occupied_sqft: u.kpiOccupiedSqft,
      occupancy_pct: u.kpiOccupancyPct,
    }))
  ), daysInMonth)

  return { units: processed, groups, kpi, has_transient_data, has_fuel_data, is_mtd: isMtd, unitTypeCounts, yield_comparison }
}

// ─── KPI Computation ─────────────────────────────────────────────────

export function computeKpis(units: ProcessedUnit[]): KpiData {
  let totalSqft = 0
  let sumOccupiedSqft = 0
  let leaseRevenue = 0
  let transientRevenue = 0
  let fuelRevenue = 0
  let producingUnits = 0
  let idleUnits = 0
  let underpricedUnits = 0
  let expiringCount = 0
  let expiringRisk = 0
  let m2mCount = 0

  for (const u of units) {
    totalSqft += u.sqft
    sumOccupiedSqft += u.occupied_sqft
    const isProducing = u.status !== 'idle' && u.status !== 'maintenance'
    if (isProducing) {
      producingUnits++
    }
    leaseRevenue += u.lease_rate || 0
    transientRevenue += u.transient_revenue || 0
    fuelRevenue += u.fuel_revenue || 0

    if (u.status === 'idle') idleUnits++
    if (u.is_underpriced) underpricedUnits++
    if (u.status === 'expiring') {
      expiringCount++
      expiringRisk += u.lease_rate || 0
    }
    if (u.status === 'month_to_month') m2mCount++
  }

  const totalYield = leaseRevenue + transientRevenue
  const totalTenantValue = totalYield + fuelRevenue
  const occupancyPct = totalSqft > 0 ? Math.round((sumOccupiedSqft / totalSqft) * 100) : 0

  // Opportunity: idle units at avg yield/sqft + underpriced gap
  const producingWithYield = units.filter(
    (u) => u.revenue_segment !== 'idle' && u.yield_per_sqft > 0
  )
  const avgYield = producingWithYield.length > 0
    ? producingWithYield.reduce((s, u) => s + u.yield_per_sqft, 0) / producingWithYield.length
    : 0

  const idleSqft = units.filter((u) => u.status === 'idle').reduce((s, u) => s + u.sqft, 0)
  const opportunityMonthly = idleSqft * avgYield

  return {
    total_yield: totalYield,
    yield_annualized: totalYield * 12,
    lease_revenue: leaseRevenue,
    transient_revenue: transientRevenue,
    fuel_revenue: fuelRevenue,
    total_tenant_value: totalTenantValue,
    occupancy_pct: occupancyPct,
    producing_sqft: sumOccupiedSqft,
    total_sqft: totalSqft,
    producing_units: producingUnits,
    total_units: units.length,
    opportunity_monthly: opportunityMonthly,
    opportunity_annualized: opportunityMonthly * 12,
    idle_units: idleUnits,
    underpriced_units: underpricedUnits,
    expiring_count: expiringCount,
    expiring_risk: expiringRisk,
    m2m_count: m2mCount,
  }
}

// ─── Grouping ────────────────────────────────────────────────────────

export function groupUnits(units: ProcessedUnit[], groupBy: GroupByOption, daysInMonth: number): UnitGroup[] {
  if (groupBy === 'none') {
    return [buildGroup('all', 'All Units', units, daysInMonth)]
  }

  const map = new Map<string, ProcessedUnit[]>()
  const labels = new Map<string, string>()

  for (const u of units) {
    let key: string
    let label: string

    switch (groupBy) {
      case 'building':
        key = u.area_id
        label = u.area_name
        break
      case 'type':
        key = u.unit_type || 'unknown'
        label = formatUnitType(u.unit_type)
        break
      case 'status':
        key = u.status
        label = formatStatus(u.status)
        break
      case 'segment':
        key = u.revenue_segment
        label = formatSegment(u.revenue_segment)
        break
      default:
        key = 'all'
        label = 'All'
    }

    const arr = map.get(key) || []
    arr.push(u)
    map.set(key, arr)
    if (!labels.has(key)) labels.set(key, label)
  }

  return Array.from(map.entries()).map(([key, groupUnits]) =>
    buildGroup(key, labels.get(key) || key, groupUnits, daysInMonth)
  )
}

function buildGroup(key: string, label: string, units: ProcessedUnit[], daysInMonth: number): UnitGroup {
  const totalSqft = units.reduce((s, u) => s + u.sqft, 0)
  const occupiedSqft = units.reduce((s, u) => s + u.occupied_sqft, 0)
  const leaseRev = units.reduce((s, u) => s + (u.lease_rate || 0), 0)
  const transRev = units.reduce((s, u) => s + (u.transient_revenue || 0), 0)
  const fuelRev = units.reduce((s, u) => s + (u.fuel_revenue || 0), 0)
  const totalRev = leaseRev + transRev
  const totalTenantValue = totalRev + fuelRev

  // Per-group yield comparison (lease vs transient vs combined)
  // Uses revenue-presence instead of revenue_segment so shared units
  // (both lease + transient) contribute to BOTH rows.
  let ycLeaseSqft = 0, ycLeaseSpaceRev = 0, ycLeaseFuelRev = 0
  let ycTransSqft = 0, ycTransSpaceRev = 0, ycTransFuelRev = 0
  let ycTransNightsWeighted = 0, ycTransTotalNights = 0
  let ycTransRateWeighted = 0, ycTransRateSqft = 0
  for (const u of units) {
    const lr = u.lease_rate || 0
    const tr = u.transient_revenue || 0
    const fr = u.fuel_revenue || 0
    const hasLease = lr > 0
    const hasTrans = tr > 0

    if (hasLease) {
      ycLeaseSqft += u.sqft
      ycLeaseSpaceRev += lr
    }
    if (hasTrans) {
      ycTransSqft += u.sqft
      ycTransSpaceRev += tr
      if (u.transient_overlap_nights != null) {
        ycTransNightsWeighted += u.transient_overlap_nights * u.sqft
        ycTransTotalNights += u.transient_overlap_nights
      }
      if (u.trans_rate_per_night != null) {
        ycTransRateWeighted += u.trans_rate_per_night * u.sqft
        ycTransRateSqft += u.sqft
      }
    }

    // Split fuel proportionally when unit has both revenue types
    if (hasLease && hasTrans) {
      const total = lr + tr
      ycLeaseFuelRev += fr * (lr / total)
      ycTransFuelRev += fr * (tr / total)
    } else if (hasLease) {
      ycLeaseFuelRev += fr
    } else if (hasTrans) {
      ycTransFuelRev += fr
    }
  }
  // Combined uses union sqft (deduplicated) to avoid double-counting shared units
  const ycCombinedSqft = units.reduce((s, u) =>
    ((u.lease_rate || 0) > 0 || (u.transient_revenue || 0) > 0) ? s + u.sqft : s, 0)
  const ycCombinedSpaceRev = ycLeaseSpaceRev + ycTransSpaceRev
  const ycCombinedFuelRev = ycLeaseFuelRev + ycTransFuelRev

  const yieldComparison: YieldComparison = {
    lease: {
      space_yield: ycLeaseSqft > 0 ? ycLeaseSpaceRev / ycLeaseSqft : 0,
      fuel_yield: ycLeaseSqft > 0 ? ycLeaseFuelRev / ycLeaseSqft : 0,
      sqft: ycLeaseSqft,
    },
    transient: {
      space_yield: ycTransSqft > 0 ? ycTransSpaceRev / ycTransSqft : 0,
      fuel_yield: ycTransSqft > 0 ? ycTransFuelRev / ycTransSqft : 0,
      sqft: ycTransSqft,
      utilization_pct: ycTransSqft > 0 && daysInMonth > 0
        ? Math.min(100, Math.round((ycTransNightsWeighted / (daysInMonth * ycTransSqft)) * 100))
        : undefined,
      avg_rate_per_night: ycTransRateSqft > 0 ? ycTransRateWeighted / ycTransRateSqft : undefined,
      occupied_nights: ycTransTotalNights > 0 ? ycTransTotalNights : undefined,
    },
    combined: {
      space_yield: ycCombinedSqft > 0 ? ycCombinedSpaceRev / ycCombinedSqft : 0,
      fuel_yield: ycCombinedSqft > 0 ? ycCombinedFuelRev / ycCombinedSqft : 0,
      sqft: ycCombinedSqft,
    },
  }

  // Building-level aggregates
  const idleCount = units.filter((u) => u.status === 'idle').length
  const hasTransientData = units.some((u) => (u.transient_revenue ?? 0) > 0)

  // Earliest lease end & days to expiry across all units
  let earliestLeaseEnd: string | undefined
  let earliestDaysToExpiry: number | undefined
  for (const u of units) {
    if (u.lease_end && (!earliestLeaseEnd || u.lease_end < earliestLeaseEnd)) {
      earliestLeaseEnd = u.lease_end
    }
    if (u.days_to_expiry !== undefined && (earliestDaysToExpiry === undefined || u.days_to_expiry < earliestDaysToExpiry)) {
      earliestDaysToExpiry = u.days_to_expiry
    }
  }

  // Client summary: count distinct lease clients + transient stays
  const leaseClients = new Set<string>()
  let transientUnitCount = 0
  for (const u of units) {
    if (u.revenue_segment === 'lease' && u.tenant_name) leaseClients.add(u.tenant_name)
    if (u.revenue_segment === 'transient') transientUnitCount++
  }
  let clientSummary: string
  if (leaseClients.size > 0 && transientUnitCount > 0) {
    clientSummary = `${leaseClients.size}+${transientUnitCount} tran`
  } else if (leaseClients.size > 0) {
    clientSummary = `${leaseClients.size} ${leaseClients.size === 1 ? 'lease' : 'leases'}`
  } else if (transientUnitCount > 0) {
    clientSummary = `${transientUnitCount} transient`
  } else {
    clientSummary = `${units.length} units`
  }

  return {
    key,
    label,
    units,
    total_units: units.length,
    total_sqft: totalSqft,
    occupied_sqft: occupiedSqft,
    occupancy_pct: totalSqft > 0 ? Math.round((occupiedSqft / totalSqft) * 100) : 0,
    lease_revenue: leaseRev,
    transient_revenue: transRev,
    total_revenue: totalRev,
    fuel_revenue: fuelRev,
    total_tenant_value: totalTenantValue,
    avg_yield: totalSqft > 0 ? totalRev / totalSqft : 0,
    yield_comparison: yieldComparison,
    is_shared: false,  // set by enrichBuildingGroups
    sub_rows: [],      // set by enrichBuildingGroups
    client_summary: clientSummary,
    idle_count: idleCount,
    earliest_lease_end: earliestLeaseEnd,
    earliest_days_to_expiry: earliestDaysToExpiry,
    has_transient_data: hasTransientData,
  }
}

// ─── Two-Tier Building Sub-Rows ─────────────────────────────────────

/** Check if a unit is "shared" (area-based, stackable, or multi-lease) */
function isSharedUnit(unit: ProcessedUnit, leases: RawLease[]): boolean {
  return (
    unit.capacity_strategy === 'area_based' ||
    unit.capacity_strategy === 'stackable' ||
    leases.length > 1
  )
}

/**
 * Compute sub-rows for a building group's expanded view.
 * Shared units → per-lease rows + transient aggregate.
 * Regular units → wrapped ProcessedUnit rows.
 */
function computeSubRows(
  group: UnitGroup,
  leasesByUnit: Map<string, RawLease[]>,
  transientStayByUnit: Map<string, RawTransientStay[]>,
  monthStart: Date,
  monthEnd: Date,
  daysInMonth: number,
  fuelRevByLease: Map<string, number>,
  fuelGalByLease: Map<string, number>,
  fuelRevByTransientStay: Map<string, number>,
  fuelGalByTransientStay: Map<string, number>,
): { subRows: DisplaySubRow[]; isShared: boolean } {
  const subRows: DisplaySubRow[] = []
  let hasAnyShared = false

  // Separate shared and regular units
  const sharedUnits: ProcessedUnit[] = []
  const regularUnits: ProcessedUnit[] = []

  for (const u of group.units) {
    const leases = leasesByUnit.get(u.unit_id) || []
    if (isSharedUnit(u, leases)) {
      sharedUnits.push(u)
      hasAnyShared = true
    } else {
      regularUnits.push(u)
    }
  }

  // Process shared units: per-lease rows + transient aggregate
  if (sharedUnits.length > 0) {
    // Collect all lease rows and transient data across shared units
    const leaseRows: LeaseDisplayRow[] = []
    let totalTransStays = 0
    let totalTransNights = 0
    let totalTransRevenue = 0
    let totalTransSqft = 0

    for (const u of sharedUnits) {
      const leases = leasesByUnit.get(u.unit_id) || []
      const transientStays = transientStayByUnit.get(u.unit_id) || []

      // Per-lease rows — equal sqft split so yield differences are visible
      const sqftPerLease = leases.length > 0 ? u.sqft / leases.length : u.sqft
      for (const lease of leases) {
        const allocatedSqft = sqftPerLease
        const yieldPerSqft = allocatedSqft > 0 ? lease.monthly_rate / allocatedSqft : 0

        let daysToExpiry: number | undefined
        if (lease.type !== 'month_to_month' && lease.end_date) {
          const d = differenceInCalendarDays(parseISO(lease.end_date), monthEnd)
          if (d >= 0) daysToExpiry = d
        }

        const tenantName = getClientName(lease.clients) || 'Unknown'

        // Classify status for this individual lease
        let leaseStatus: UnitStatus = 'leased'
        if (lease.type === 'month_to_month') {
          leaseStatus = 'month_to_month'
        } else if (daysToExpiry !== undefined && daysToExpiry <= 90) {
          leaseStatus = 'expiring'
        }

        const renewalStatus = deriveRenewalStatus([lease])

        // Fuel overlay per lease
        const leaseFuelRev = fuelRevByLease.get(lease.id) || 0
        const leaseFuelGal = fuelGalByLease.get(lease.id) || 0
        const leaseCommitment = lease.fuel_commitment_gallons_monthly || 0
        const leaseCommitmentPct = leaseCommitment > 0 ? Math.round((leaseFuelGal / leaseCommitment) * 100) : undefined

        leaseRows.push({
          lease_id: lease.id,
          unit_code: u.unit_code,
          sqft: Math.round(allocatedSqft),
          status: leaseStatus,
          tenant_name: tenantName,
          lease_rate: lease.monthly_rate,
          yield_per_sqft: yieldPerSqft,
          lease_end: lease.end_date || undefined,
          days_to_expiry: daysToExpiry,
          lease_type: lease.type,
          contract_number: lease.contract_number,
          renewal_status: renewalStatus,
          escalation_pct: lease.escalation_pct ?? undefined,
          escalation_type: lease.escalation_type,
          annual_value: lease.monthly_rate * 12,
          is_underpriced: false, // underpriced detection at unit level only
          rate_gap_pct: u.rate_gap_pct,
          // Fuel overlay
          fuel_revenue: leaseFuelRev > 0 ? leaseFuelRev : undefined,
          fuel_commitment_gallons: leaseCommitment > 0 ? leaseCommitment : undefined,
          fuel_delivered_gallons: leaseFuelGal > 0 ? leaseFuelGal : undefined,
          fuel_commitment_pct: leaseCommitmentPct,
        })
      }

      // Accumulate transient data for this shared unit
      if (transientStays.length > 0) {
        totalTransStays += transientStays.length
        // Lease slots take equal shares; transient gets the remainder
        const allocatedLeaseSqft = leases.length * sqftPerLease
        const remainingSqft = Math.max(0, u.sqft - allocatedLeaseSqft)
        totalTransSqft += remainingSqft > 0 ? remainingSqft : u.sqft

        for (const ts of transientStays) {
          totalTransNights += computeOverlapNights(ts, monthStart, monthEnd)
          totalTransRevenue += prorateTranRevenue(ts, monthStart, monthEnd)
        }
      }
    }

    // Add based-client section header + rows
    if (leaseRows.length > 0) {
      subRows.push({
        kind: 'section_header',
        label: `Based Clients (${leaseRows.length})`,
      })
      for (const lr of leaseRows) {
        subRows.push({ kind: 'lease', data: lr })
      }
    }

    // Add transient aggregate row
    if (totalTransStays > 0) {
      const transSqft = totalTransSqft > 0 ? totalTransSqft : 1

      // Aggregate fuel from all transient stays in shared units
      let totalTransFuelRev = 0
      let totalTransFuelGal = 0
      for (const u of sharedUnits) {
        const stays = transientStayByUnit.get(u.unit_id) || []
        for (const ts of stays) {
          totalTransFuelRev += fuelRevByTransientStay.get(ts.id) || 0
          totalTransFuelGal += fuelGalByTransientStay.get(ts.id) || 0
        }
      }

      subRows.push({
        kind: 'section_header',
        label: 'Transient Activity',
        detail: `${totalTransStays} stays · ${totalTransNights} nights`,
      })
      subRows.push({
        kind: 'transient_agg',
        data: {
          stay_count: totalTransStays,
          night_count: totalTransNights,
          sqft: Math.round(totalTransSqft),
          total_revenue: totalTransRevenue,
          yield_per_sqft: transSqft > 0 ? totalTransRevenue / transSqft : 0,
          utilization_pct: Math.min(100, Math.round((totalTransNights / (daysInMonth * sharedUnits.length)) * 100)),
          trans_rate_per_night: totalTransNights > 0 ? totalTransRevenue / totalTransNights : 0,
          fuel_revenue: totalTransFuelRev,
          fuel_gallons: totalTransFuelGal,
        } satisfies TransientAggRow,
      })
    }
  }

  // Process regular units as plain unit rows
  for (const u of regularUnits) {
    subRows.push({ kind: 'unit', data: u })
  }

  return { subRows, isShared: hasAnyShared }
}

/**
 * Enrich building groups with sub-rows and shared-building metadata.
 * Only called when groupBy === 'building'.
 */
export function enrichBuildingGroups(
  groups: UnitGroup[],
  leasesByUnit: Map<string, RawLease[]>,
  transientStayByUnit: Map<string, RawTransientStay[]>,
  monthStart: Date,
  monthEnd: Date,
  daysInMonth: number,
  fuelRevByLease: Map<string, number>,
  fuelGalByLease: Map<string, number>,
  fuelRevByTransientStay: Map<string, number>,
  fuelGalByTransientStay: Map<string, number>,
): UnitGroup[] {
  return groups.map((group) => {
    const { subRows, isShared } = computeSubRows(
      group,
      leasesByUnit,
      transientStayByUnit,
      monthStart,
      monthEnd,
      daysInMonth,
      fuelRevByLease,
      fuelGalByLease,
      fuelRevByTransientStay,
      fuelGalByTransientStay,
    )
    return { ...group, sub_rows: subRows, is_shared: isShared }
  })
}

// ─── Yield Comparison ─────────────────────────────────────────────────

/**
 * Compute facility-level yield comparison: lease vs transient vs combined.
 * Each segment shows space $/sqft and fuel $/sqft independently.
 * Uses the KPI-reprocessed units (before segment/status filters).
 */
function computeYieldComparison(units: ProcessedUnit[], daysInMonth: number): YieldComparison {
  let leaseSqft = 0, leaseSpaceRev = 0, leaseFuelRev = 0
  let transSqft = 0, transSpaceRev = 0, transFuelRev = 0
  let transNightsWeighted = 0, transTotalNights = 0
  let transRateWeighted = 0, transRateSqft = 0

  for (const u of units) {
    if (u.revenue_segment === 'lease') {
      leaseSqft += u.sqft
      leaseSpaceRev += u.lease_rate || 0
      leaseFuelRev += u.fuel_revenue || 0
    } else if (u.revenue_segment === 'transient') {
      transSqft += u.sqft
      transSpaceRev += u.transient_revenue || 0
      transFuelRev += u.fuel_revenue || 0
      if (u.transient_overlap_nights != null) {
        transNightsWeighted += u.transient_overlap_nights * u.sqft
        transTotalNights += u.transient_overlap_nights
      }
      if (u.trans_rate_per_night != null) {
        transRateWeighted += u.trans_rate_per_night * u.sqft
        transRateSqft += u.sqft
      }
    }
  }

  const combinedSqft = leaseSqft + transSqft
  const combinedSpaceRev = leaseSpaceRev + transSpaceRev
  const combinedFuelRev = leaseFuelRev + transFuelRev

  return {
    lease: {
      space_yield: leaseSqft > 0 ? leaseSpaceRev / leaseSqft : 0,
      fuel_yield: leaseSqft > 0 ? leaseFuelRev / leaseSqft : 0,
      sqft: leaseSqft,
    },
    transient: {
      space_yield: transSqft > 0 ? transSpaceRev / transSqft : 0,
      fuel_yield: transSqft > 0 ? transFuelRev / transSqft : 0,
      sqft: transSqft,
      utilization_pct: transSqft > 0 && daysInMonth > 0
        ? Math.min(100, Math.round((transNightsWeighted / (daysInMonth * transSqft)) * 100))
        : undefined,
      avg_rate_per_night: transRateSqft > 0 ? transRateWeighted / transRateSqft : undefined,
      occupied_nights: transTotalNights > 0 ? transTotalNights : undefined,
    },
    combined: {
      space_yield: combinedSqft > 0 ? combinedSpaceRev / combinedSqft : 0,
      fuel_yield: combinedSqft > 0 ? combinedFuelRev / combinedSqft : 0,
      sqft: combinedSqft,
    },
  }
}

// ─── Label Formatters ────────────────────────────────────────────────

export function formatUnitType(type: string | null): string {
  switch (type) {
    case 'conventional_hangar': return 'Conventional Hangar'
    case 'box_hangar': return 'Box Hangar'
    case 't_hangar': return 'T-Hangar'
    case 'shade': return 'Shade Port'
    case 'tiedown': return 'Tiedown'
    case 'ramp': return 'Ramp'
    case 'office': return 'Office'
    case 'storage': return 'Storage'
    case 'shop': return 'Shop / Workshop'
    case 'custom': return 'Custom'
    default: return 'Unknown'
  }
}

export function formatStatus(status: UnitStatus): string {
  switch (status) {
    case 'leased': return 'Leased'
    case 'underpriced': return 'Underpriced'
    case 'expiring': return 'Expiring'
    case 'month_to_month': return 'Month-to-Month'
    case 'transient': return 'Transient'
    case 'occupied_no_contract': return 'Occupied (No Contract)'
    case 'idle': return 'Idle'
    case 'maintenance': return 'Maintenance'
  }
}

export function formatSegment(segment: RevenueSegment): string {
  switch (segment) {
    case 'lease': return 'Lease'
    case 'transient': return 'Transient'
    case 'idle': return 'Idle'
  }
}

export function formatCurrency(val: number, decimals = 0): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: decimals,
  }).format(val)
}

export function formatYieldPerSqft(val: number): string {
  return `$${val.toFixed(2)}`
}
